// services/disputeService.js
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { sendEmail } from './notificationService';
import { getPatientApplicationLinkHtml } from '../utils/emailUtils';
import { getApplication } from './applicationService';

/**
 * Generate description from issues array
 * @param {Array} issues - Array of issue objects
 * @returns {string} Generated description
 */
const generateDescriptionFromIssues = (issues) => {
  if (!issues || issues.length === 0) {
    return 'No specific issues provided';
  }
  
  if (issues.length === 1) {
    return issues[0].description || issues[0].title || 'Single issue raised';
  }
  
  return `Multiple issues raised: ${issues.map((issue, index) => 
    `${index + 1}. ${issue.title || issue.description || 'Issue'}`
  ).join('; ')}`;
};

/**
 * Create a new dispute/query for an application
 * @param {Object} disputeData - Query information
 * @returns {Promise} Result object with success status
 */
export const createDispute = async (disputeData) => {
  try {
    const dispute = {
      applicationId: disputeData.applicationId,
      raisedBy: {
        userId: disputeData.raisedBy.userId,
        userName: disputeData.raisedBy.userName,
        department: disputeData.raisedBy.department, // medical, policy, financial, kyc
        role: disputeData.raisedBy.role
      },
      assignedTo: disputeData.assignedTo || 'hospital', // hospital, medical, policy, financial
      // Legacy support - derive disputeType from first issue or set to 'other'
      disputeType: disputeData.disputeType || (disputeData.issues && disputeData.issues.length > 0 ? disputeData.issues[0].type : 'other'),
      priority: disputeData.priority || 'medium', // low, medium, high, urgent
      status: 'raised', // raised, acknowledged, under_review, resolved, escalated
      title: disputeData.title || 'Multiple Issues Raised',
      description: disputeData.description || generateDescriptionFromIssues(disputeData.issues),
      
      // Multiple issues support
      issues: disputeData.issues || [], // Array of issue objects with type, description, priority
      
      // Form fields for resolution
      requiredFields: disputeData.requiredFields || [], // Array of field objects
      resolverResponse: null,
      resolverDocuments: [],
      
      // Timeline tracking
      raisedAt: Timestamp.now(),
      acknowledgedAt: null,
      resolvedAt: null,
      lastUpdatedAt: Timestamp.now(),
      
      // Communication thread
      communications: [{
        from: disputeData.raisedBy.userId,
        fromName: disputeData.raisedBy.userName,
        fromDepartment: disputeData.raisedBy.department,
        message: disputeData.description || generateDescriptionFromIssues(disputeData.issues),
        issues: disputeData.issues || [],
        timestamp: Timestamp.now(),
        type: 'dispute_raised'
      }],
      
      // Escalation
      escalationLevel: 0,
      escalatedTo: null,
      escalatedAt: null,
      
      // Resolution
      resolution: null,
      resolvedBy: null,
      satisfactionRating: null,
      
      // Internal tracking
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'disputes'), dispute);
    
    // Update application status to indicate dispute
    await updateApplicationDisputeStatus(disputeData.applicationId, 'dispute_raised', docRef.id);
    
    // Create notification for all stakeholders
    await createDisputeNotifications(disputeData.applicationId, docRef.id, 'dispute_raised', dispute);
    
    return {
      success: true,
      disputeId: docRef.id,
      message: 'Query raised successfully'
    };
  } catch (error) {
    console.error('Error creating dispute:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get disputes for a specific application
 * @param {string} applicationId - Application ID
 * @returns {Promise} Result with disputes array
 */
export const getApplicationDisputes = async (applicationId) => {
  try {
    const q = query(
      collection(db, 'disputes'),
      where('applicationId', '==', applicationId),
      orderBy('raisedAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const disputes = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      raisedAt: doc.data().raisedAt?.toDate(),
      acknowledgedAt: doc.data().acknowledgedAt?.toDate(),
      resolvedAt: doc.data().resolvedAt?.toDate(),
      lastUpdatedAt: doc.data().lastUpdatedAt?.toDate()
    }));
    
    return {
      success: true,
      disputes
    };
  } catch (error) {
    console.error('Error fetching disputes:', error);
    return {
      success: false,
      error: error.message,
      disputes: []
    };
  }
};

/**
 * Get disputes assigned to a specific user/department
 * @param {Object} params - Filter parameters
 * @returns {Promise} Result with disputes array
 */
export const getAssignedDisputes = async (params) => {
  try {
    let q;
    
    if (params.department) {
      q = query(
        collection(db, 'disputes'),
        where('assignedTo', '==', params.department),
        where('status', 'in', ['raised', 'acknowledged', 'under_review']),
        orderBy('priority', 'desc'),
        orderBy('raisedAt', 'desc')
      );
    } else if (params.userId) {
      q = query(
        collection(db, 'disputes'),
        where('raisedBy.userId', '==', params.userId),
        orderBy('raisedAt', 'desc')
      );
    }
    
    const snapshot = await getDocs(q);
    const disputes = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      raisedAt: doc.data().raisedAt?.toDate(),
      acknowledgedAt: doc.data().acknowledgedAt?.toDate(),
      resolvedAt: doc.data().resolvedAt?.toDate(),
      lastUpdatedAt: doc.data().lastUpdatedAt?.toDate()
    }));
    
    return {
      success: true,
      disputes
    };
  } catch (error) {
    console.error('Error fetching assigned disputes:', error);
    return {
      success: false,
      error: error.message,
      disputes: []
    };
  }
};

/**
 * Update dispute status and add communication
 * @param {string} disputeId - Query ID
 * @param {Object} updateData - Update information
 * @returns {Promise} Result object
 */
export const updateDisputeStatus = async (disputeId, updateData) => {
  try {
    const disputeRef = doc(db, 'disputes', disputeId);
    const disputeDoc = await getDoc(disputeRef);
    
    if (!disputeDoc.exists()) {
      return {
        success: false,
        error: 'Query not found'
      };
    }
    
    const currentData = disputeDoc.data();
    // Prevent further status changes if already accepted (finalized)
    if (currentData.accepted) {
      // Allow adding a communication without changing status
      if (updateData.status && updateData.status !== currentData.status) {
        return { success: false, error: 'Query has been accepted and is locked from further status changes' };
      }
    }

    const updateObj = {
      status: updateData.status,
      lastUpdatedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    // Add communication entry
    if (updateData.message) {
      updateObj.communications = arrayUnion({
        from: updateData.userId,
        fromName: updateData.userName,
        fromDepartment: updateData.department,
        message: updateData.message,
        timestamp: Timestamp.now(),
        type: updateData.status
      });
    }
    
    // Handle specific status updates
    switch (updateData.status) {
      case 'acknowledged':
        updateObj.acknowledgedAt = Timestamp.now();
        break;
      case 'resolved':
        updateObj.resolvedAt = Timestamp.now();
        updateObj.resolvedBy = {
          userId: updateData.userId,
          userName: updateData.userName,
          department: updateData.department
        };
        if (updateData.resolution) {
          updateObj.resolution = updateData.resolution;
        }
        if (updateData.resolverResponse) {
          updateObj.resolverResponse = updateData.resolverResponse;
        }
        break;
      case 'escalated':
        updateObj.escalationLevel = (currentData.escalationLevel || 0) + 1;
        updateObj.escalatedAt = Timestamp.now();
        updateObj.escalatedTo = updateData.escalatedTo;
        break;
    }
    
    await updateDoc(disputeRef, updateObj);
    
    // Update application dispute status
    if (updateData.status === 'resolved') {
      await updateApplicationDisputeStatus(currentData.applicationId, 'dispute_resolved', disputeId);
    }
    
    // Create notifications
    await createDisputeNotifications(currentData.applicationId, disputeId, updateData.status, updateObj);
    
    return {
      success: true,
      message: 'Query updated successfully'
    };
  } catch (error) {
    console.error('Error updating dispute:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Resolve dispute with form response
 * @param {string} disputeId - Query ID
 * @param {Object} responseData - Response form data
 * @returns {Promise} Result object
 */
export const resolveDisputeWithResponse = async (disputeId, responseData) => {
  try {
    const disputeRef = doc(db, 'disputes', disputeId);
    const disputeDoc = await getDoc(disputeRef);
    
    if (!disputeDoc.exists()) {
      return {
        success: false,
        error: 'Query not found'
      };
    }
    
    const currentData = disputeDoc.data();
    
    // Validate that all required fields are provided
    const requiredFields = currentData.requiredFields || [];
    const missingFields = requiredFields.filter(field => 
      !responseData.formData[field.name] || responseData.formData[field.name].trim() === ''
    );
    
    if (missingFields.length > 0) {
      return {
        success: false,
        error: `Missing required fields: ${missingFields.map(f => f.label).join(', ')}`
      };
    }

    // Sanitize form data to remove File objects before storing in Firestore
    const sanitizedFormData = {};
    Object.entries(responseData.formData).forEach(([key, value]) => {
      // Skip File objects - they should already be processed into fileMetadata
      if (value instanceof File) {
        console.warn(`Skipping File object for key: ${key}. File should be uploaded and converted to metadata.`);
        return;
      }
      
      // Include all other data
      sanitizedFormData[key] = value;
    });
    
    const updateObj = {
      status: 'resolved',
      resolvedAt: Timestamp.now(),
      lastUpdatedAt: Timestamp.now(),
      resolvedBy: {
        userId: responseData.userId,
        userName: responseData.userName,
        department: responseData.department || 'hospital'
      },
      resolverResponse: sanitizedFormData, // Use sanitized data instead of raw formData
      resolution: responseData.resolution || 'Resolved with provided information',
      communications: arrayUnion({
        from: responseData.userId,
        fromName: responseData.userName,
        fromDepartment: responseData.department || 'hospital',
        message: `Query resolved: ${responseData.resolution || 'Information provided as requested'}`,
        timestamp: Timestamp.now(),
        type: 'resolved'
      })
    };
    
    await updateDoc(disputeRef, updateObj);
    
    // Update application status
    await updateApplicationDisputeStatus(currentData.applicationId, 'dispute_resolved', disputeId);
    
    // Create notifications
    await createDisputeNotifications(currentData.applicationId, disputeId, 'resolved', updateObj);

    // Fire-and-forget: Email routing based on which department raised the query
    (async () => {
      try {
        const raiserDept = (currentData?.raisedBy?.department || '').toLowerCase();
        const to = raiserDept === 'medical' || raiserDept === 'doctor' ? 'doctors@samhealth.co.in' : 'cssg@samhealth.co.in';
        const subject = `Query Resolved: ${currentData.applicationId}`;
        
        // Get application details to include patient name in link
        let patientName = 'Patient';
        let recipientRole = raiserDept === 'medical' || raiserDept === 'doctor' ? 'doctor' : 'finance';
        try {
          const appResult = await getApplication(currentData.applicationId);
          if (appResult.success && appResult.application) {
            patientName = appResult.application.patient?.name || 'Patient';
          }
        } catch (e) {
          console.warn('Could not fetch application for email link:', e);
        }
        
        const patientLink = getPatientApplicationLinkHtml(
          currentData.applicationId,
          patientName,
          to,
          recipientRole,
          `View Patient Application for ${patientName} (${currentData.applicationId})`
        );
        
        const html = `<!DOCTYPE html><html><body style="font-family:Arial,Helvetica,sans-serif;line-height:1.5;color:#0f172a;">
          <h2 style=\"color:#0f766e;margin:0 0 12px;\">Query Resolved</h2>
          <p style=\"margin:0 0 8px;\">A hospital response has resolved a query for application <strong>${currentData.applicationId}</strong>.</p>
          ${patientLink}
          <p style=\"margin:8px 0 0;font-size:12px;color:#64748b;\">This is an automated notification.</p>
        </body></html>`;
        await sendEmail({ to, subject, html });
      } catch (e) {
        console.warn('Non-blocking email failed (resolveDisputeWithResponse):', e?.message || e);
      }
    })();
    
    return {
      success: true,
      message: 'Query resolved successfully'
    };
  } catch (error) {
    console.error('Error resolving dispute:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Accept a resolved dispute response (finalize & lock further edits)
 * - Marks the dispute as accepted by the raiser
 * - Leaves status as 'resolved' but sets accepted flag and audit fields
 * @param {string} disputeId
 * @param {{userId:string,userName:string,department:string,message?:string}} acceptanceData
 */
export const acceptDisputeResponse = async (disputeId, acceptanceData) => {
  try {
    const disputeRef = doc(db, 'disputes', disputeId);
    const disputeDoc = await getDoc(disputeRef);
    if (!disputeDoc.exists()) {
      return { success: false, error: 'Query not found' };
    }

    const current = disputeDoc.data();

    // If not resolved yet, do not accept (UI should prevent this)
    if (current.status !== 'resolved') {
      return { success: false, error: 'Query must be resolved before acceptance' };
    }

    const updateObj = {
      // keep status as resolved, but mark acceptance
      accepted: true,
      acceptedAt: Timestamp.now(),
      acceptedBy: {
        userId: acceptanceData.userId,
        userName: acceptanceData.userName,
        department: acceptanceData.department
      },
      lastUpdatedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      communications: arrayUnion({
        from: acceptanceData.userId,
        fromName: acceptanceData.userName,
        fromDepartment: acceptanceData.department,
        message: acceptanceData.message || 'Response accepted by raiser',
        timestamp: Timestamp.now(),
        type: 'accepted'
      })
    };

    await updateDoc(disputeRef, updateObj);

    // Optional notification for acceptance event
    await createDisputeNotifications(current.applicationId, disputeId, 'accepted', {
      ...current,
      ...updateObj
    });

    return { success: true, message: 'Query response accepted' };
  } catch (error) {
    console.error('Error accepting dispute response:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update application status when dispute is raised/resolved
 * @param {string} applicationId - Application ID
 * @param {string} disputeStatus - Query status
 * @param {string} disputeId - Query ID
 */
const updateApplicationDisputeStatus = async (applicationId, disputeStatus, disputeId) => {
  try {
    // Find application document
    const applicationsQuery = query(
      collection(db, 'patientApplications'),
      where('applicationId', '==', applicationId)
    );
    
    const snapshot = await getDocs(applicationsQuery);
    if (!snapshot.empty) {
      const appDoc = snapshot.docs[0];
      const currentData = appDoc.data();
      
      let updateObj = {
        updatedAt: Timestamp.now()
      };
      
      if (disputeStatus === 'dispute_raised') {
        updateObj.disputeStatus = 'active';
        updateObj.activeDisputes = arrayUnion(disputeId);
        updateObj.previousStatus = currentData.status; // Save current status before blocking
        updateObj.status = 'dispute_pending'; // Block further processing
      } else if (disputeStatus === 'dispute_resolved') {
        const activeDisputes = currentData.activeDisputes || [];
        const remainingDisputes = activeDisputes.filter(id => id !== disputeId);
        
        updateObj.activeDisputes = remainingDisputes;
        if (remainingDisputes.length === 0) {
          updateObj.disputeStatus = 'resolved';
          // Restore the previous status properly based on the application's workflow state
          const previousStatus = currentData.previousStatus;
          const finApproved = (currentData.workflow?.financialVerification?.status === 'approved')
            || currentData.status === 'financial_approved'
            || currentData.status === 'approved';

          if (finApproved) {
            // If already financially approved (or fully approved), do NOT downgrade
            updateObj.status = currentData.status === 'approved' ? 'approved' : 'financial_approved';
          } else if (previousStatus) {
            updateObj.status = previousStatus;
          } else {
            // Fallback logic if previousStatus is not available
            const workflowStatus = currentData.workflow?.medicalVerification?.status;
            if (workflowStatus === 'pending') {
              updateObj.status = 'submitted';
            } else if (workflowStatus === 'approved') {
              updateObj.status = 'medical_approved';
            } else {
              updateObj.status = 'under_review';
            }
          }
          // Clear the previousStatus since it's no longer needed
          updateObj.previousStatus = null;
          
          // Also restore workflow state if needed
          if (!updateObj.status && currentData.workflow?.medicalVerification?.status === 'pending' && 
              (updateObj.status === 'submitted' || updateObj.status === 'under_review')) {
            // If medical verification is still pending, ensure it's available for review
            updateObj['workflow.medicalReview.status'] = 'pending';
            updateObj['workflow.medicalReview.reviewerId'] = null;
            updateObj['workflow.medicalReview.reviewStartTime'] = null;
            updateObj['workflow.medicalReview.lastActivityTime'] = null;
            // Set appropriate status for medical review
            if (!updateObj.status || updateObj.status === 'under_review') {
              updateObj.status = 'submitted'; // Make it available for fresh medical review
            }
          }
          
          // If medical verification was already approved, restore to medical_approved
          if (!updateObj.status && currentData.workflow?.medicalVerification?.status === 'approved') {
            updateObj.status = 'medical_approved';
          }
          
          // If in policy phase, restore to policy review status
          if (!updateObj.status && currentData.workflow?.policyVerification?.status === 'pending') {
            updateObj.status = 'policy_review_pending';
          }
          
          // If in financial phase, restore to financial review status  
          if (!updateObj.status && currentData.workflow?.financialVerification?.status === 'pending') {
            updateObj.status = 'financial_review_pending';
          }
        }
      }
      
      await updateDoc(doc(db, 'patientApplications', appDoc.id), updateObj);
    }
  } catch (error) {
    console.error('Error updating application dispute status:', error);
  }
};

/**
 * Create notifications for dispute events
 * @param {string} applicationId - Application ID
 * @param {string} disputeId - Query ID
 * @param {string} eventType - Event type
 * @param {Object} disputeData - Query data
 */
const createDisputeNotifications = async (applicationId, disputeId, eventType, disputeData) => {
  try {
    // Get application details to find stakeholders
    const applicationsQuery = query(
      collection(db, 'patientApplications'),
      where('applicationId', '==', applicationId)
    );
    
    const snapshot = await getDocs(applicationsQuery);
    if (!snapshot.empty) {
      const appData = snapshot.docs[0].data();
      
      // Create notifications for all stakeholders
      const notifications = [];
      
      // Hospital notification - find hospital users for this hospital
      if (appData.hospitalId) {
        try {
          // Find all users associated with this hospital
          const hospitalUsersQuery = query(
            collection(db, 'users'),
            where('role', '==', 'Hospital'),
            where('hospitalId', '==', appData.hospitalId)
          );
          
          const hospitalUsersSnapshot = await getDocs(hospitalUsersQuery);
          
          // Create notification for each hospital user
          hospitalUsersSnapshot.docs.forEach(userDoc => {
            notifications.push({
              recipientId: userDoc.id, // This is the user's UID
              recipientType: 'user',
              userRole: 'Hospital',
              type: 'dispute',
              subType: eventType,
              title: getNotificationTitle(eventType),
              message: getNotificationMessage(eventType, disputeData),
              applicationId,
              disputeId,
              priority: disputeData.priority || 'medium',
              read: false,
              createdAt: Timestamp.now(),
              metadata: {
                hospitalId: appData.hospitalId,
                hospitalName: appData.hospitalName || 'Unknown Hospital'
              }
            });
          });
          
          console.log(`Created notifications for ${hospitalUsersSnapshot.docs.length} hospital users`);
        } catch (error) {
          console.error('Error finding hospital users:', error);
        }
      }
      
      // Department notifications (medical, policy, financial)
      const departments = ['medical', 'policy', 'financial'];
      const roleMapping = {
        'medical': 'Doctor',
        'policy': 'PolicyWriter', 
        'financial': 'FinancialTeam'
      };
      
      for (const dept of departments) {
        try {
          // Find all users with the corresponding role
          const deptUsersQuery = query(
            collection(db, 'users'),
            where('role', '==', roleMapping[dept])
          );
          
          const deptUsersSnapshot = await getDocs(deptUsersQuery);
          
          // Create notification for each department user
          deptUsersSnapshot.docs.forEach(userDoc => {
            notifications.push({
              recipientId: userDoc.id, // This is the user's UID
              recipientType: 'user',
              userRole: roleMapping[dept],
              department: dept,
              type: 'dispute',
              subType: eventType,
              title: getNotificationTitle(eventType),
              message: getNotificationMessage(eventType, disputeData),
              applicationId,
              disputeId,
              priority: disputeData.priority || 'medium',
              read: false,
              createdAt: Timestamp.now()
            });
          });
          
          console.log(`Created notifications for ${deptUsersSnapshot.docs.length} ${dept} users`);
        } catch (error) {
          console.error(`Error finding ${dept} users:`, error);
        }
      }
      
      // Save all notifications
      for (const notification of notifications) {
        await addDoc(collection(db, 'notifications'), notification);
      }
      
      console.log(`Total notifications created: ${notifications.length}`);
    }
  } catch (error) {
    console.error('Error creating dispute notifications:', error);
  }
};

/**
 * Get notification title based on event type
 */
const getNotificationTitle = (eventType) => {
  const titles = {
    dispute_raised: '🚨 New Query Raised',
    acknowledged: '✅ Query Acknowledged',
    under_review: '🔍 Query Under Review',
  resolved: '✅ Query Resolved',
  accepted: '✅ Query Response Accepted',
    escalated: '⚠️ Query Escalated'
  };
  return titles[eventType] || '📋 Query Update';
};

/**
 * Get notification message based on event type
 */
const getNotificationMessage = (eventType, disputeData) => {
  const messages = {
    dispute_raised: `A new dispute has been raised requiring your attention. Please review and respond.`,
    acknowledged: `The dispute has been acknowledged and is being reviewed.`,
    under_review: `The dispute is currently under review by the assigned team.`,
  resolved: `The dispute has been resolved. You can proceed with the application.`,
  accepted: `The dispute response has been accepted by the raiser. This query is finalized.`,
    escalated: `The dispute has been escalated for higher-level review.`
  };
  return messages[eventType] || 'Query status has been updated.';
};

/**
 * Check if application has active disputes
 * @param {string} applicationId - Application ID
 * @returns {Promise} Result with dispute status
 */
export const checkApplicationDisputeStatus = async (applicationId, options = {}) => {
  try {
    const q = query(
      collection(db, 'disputes'),
      where('applicationId', '==', applicationId),
      where('status', 'in', ['raised', 'acknowledged', 'under_review'])
    );
    
    const snapshot = await getDocs(q);
    const activeDisputes = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Optional department scoping: Only block the process for the department that raised the query
    // Normalize department names to handle variations (doctor->medical, finance->financial)
    const normalizeDept = (d) => {
      const normalized = (d || '').toLowerCase().trim();
      if (normalized === 'doctor') return 'medical';
      if (normalized === 'finance') return 'financial';
      return normalized;
    };
    const scopeDept = options?.department ? normalizeDept(options.department) : null;
    const scoped = scopeDept
      ? activeDisputes.filter(d => normalizeDept(d?.raisedBy?.department) === scopeDept)
      : activeDisputes;
    
    // When department is provided, return scoped results
    // When no department provided, return all active disputes (backward compatible)
    const relevantDisputes = scopeDept ? scoped : activeDisputes;
    
    return {
      success: true,
      hasActiveDisputes: relevantDisputes.length > 0,
      activeDisputes: relevantDisputes,
      canProceed: relevantDisputes.length === 0,
      scopedActiveDisputes: scopeDept ? scoped : undefined,
      allActiveDisputes: activeDisputes // Include all disputes for reference
    };
  } catch (error) {
    console.error('Error checking dispute status:', error);
    return {
      success: false,
      error: error.message,
      hasActiveDisputes: false,
      activeDisputes: [],
      canProceed: false
    };
  }
};

export default {
  createDispute,
  getApplicationDisputes,
  getAssignedDisputes,
  updateDisputeStatus,
  resolveDisputeWithResponse,
  acceptDisputeResponse,
  checkApplicationDisputeStatus
};
